// Operator audit log helper.
//
// Fire-and-forget. The backend stamps the actor + IP from the authenticated
// session, so callers only supply { action, targetType, targetId, metadata? }.
// Failures never block the user-facing action — they're logged to console and
// swallowed. The audit log is for observability, not correctness.

export type AuditAction =
  | 'deploy'
  | 'deploy_batch'
  | 'retry'
  | 'cancel'
  | 'reconcile_stale'
  | 'schedule_create'
  | 'schedule_cancel'
  | 'schedule_fire'
  | 'publish_release_note'
  | 'save_release_note_draft'
  | 'delete_release_note'
  | 'toggle_notifications'
  // CLIENT-SIDE SYNTHETIC VALUE — never sent over the wire to /api/audit
  // and never returned from it. The Audit Log page injects rows with this
  // action by mapping entries from the backend's /api/logs/flush-history
  // (Redis-backed) into the grid, so PM2 flushes appear alongside worker
  // audit rows without us having to add a new value to the deployed
  // worker's Zod enum. If this value is ever sent to logAudit() the
  // POST will be rejected — keep it purely for in-memory shaping.
  | 'pm2_log_flush';

export type AuditTargetType =
  | 'build'
  | 'pipeline'
  | 'app'
  | 'release_note'
  | 'schedule'
  | 'batch'
  | 'session';

export interface AuditEntry {
  action: AuditAction;
  targetType: AuditTargetType;
  targetId: string | number;
  metadata?: Record<string, unknown>;
}

/**
 * Record an operator action.
 *
 * Returns `true` when the worker accepted the write, `false` otherwise. Most
 * callers fire-and-forget and don't care — the audit log is for observability,
 * not correctness, and a failed write should never block the actual user
 * action. But callers that *do* want to react (e.g. the Audit Log page
 * wanting to refresh its grid only after the row has actually been written)
 * can await the boolean and gate on it.
 *
 * Note: a previous version of this helper treated any `fetch` resolution
 * as success — including 4xx / 5xx responses. That hid worker-side
 * validation failures (e.g. a new `action` value not yet in the deployed
 * worker's Zod enum) and looked like "audit silently broken". We now check
 * `response.ok` explicitly and log the status so it surfaces in the
 * console.
 */
export const logAudit = async (entry: AuditEntry): Promise<boolean> => {
  try {
    const tok = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
    const resp = await fetch('/api/audit', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      },
      body: JSON.stringify({
        action: entry.action,
        targetType: entry.targetType,
        targetId: String(entry.targetId),
        metadata: entry.metadata,
      }),
    });
    if (!resp.ok) {
      let detail = '';
      try { detail = (await resp.text()).slice(0, 200); } catch { /* ignore */ }
      console.warn(`[audit] write rejected (${resp.status} ${resp.statusText}) for action="${entry.action}" — likely worker enum / schema is out of date. ${detail}`);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[audit] write failed (non-fatal):', err);
    return false;
  }
};
